import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCreateProduct, useUpdateProduct, useVendors } from "./hooks";
import { PRODUCT_STATUS_LABELS } from "./labels";
import type { ProductDetail, ProductStatus } from "./api";

const EMPTY = {
  name: "",
  sku: "",
  barcode: "",
  category: "",
  unit: "each",
  unit_cost: "",
  sale_price: "",
  reorder_level: "0",
  status: "active" as ProductStatus,
  preferred_vendor_id: "",
  opening_stock: "0",
};

export function ProductFormDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: ProductDetail | null;
}) {
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const { data: vendors } = useVendors({ page_size: 100 }, { enabled: open });
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name,
        sku: editing.sku,
        barcode: editing.barcode ?? "",
        category: editing.category ?? "",
        unit: editing.unit,
        unit_cost: editing.unit_cost ?? "",
        sale_price: editing.sale_price ?? "",
        reorder_level: String(editing.reorder_level),
        status: editing.status,
        preferred_vendor_id: editing.preferred_vendor_id ?? "",
        opening_stock: "0",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setError(null);
  }, [open, editing]);

  const set = (k: keyof typeof EMPTY) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const common = {
      name: form.name.trim(),
      barcode: form.barcode.trim() || null,
      category: form.category.trim() || null,
      unit: form.unit.trim() || "each",
      unit_cost: form.unit_cost.trim() || null,
      sale_price: form.sale_price.trim() || null,
      reorder_level: Number(form.reorder_level) || 0,
      status: form.status,
      preferred_vendor_id: form.preferred_vendor_id || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body: common });
      } else {
        await create.mutateAsync({
          ...common,
          sku: form.sku.trim() || null,
          opening_stock: Number(form.opening_stock) || 0,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the product.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit product" : "Add product"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <Field label="Name" htmlFor="p_name">
          <Input id="p_name" value={form.name} onChange={(e) => set("name")(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU (optional)" htmlFor="p_sku">
            <Input
              id="p_sku"
              value={form.sku}
              disabled={editing !== null}
              placeholder="Auto if blank"
              onChange={(e) => set("sku")(e.target.value)}
            />
          </Field>
          <Field label="Barcode" htmlFor="p_barcode">
            <Input id="p_barcode" value={form.barcode} onChange={(e) => set("barcode")(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Category" htmlFor="p_cat">
            <Input id="p_cat" value={form.category} onChange={(e) => set("category")(e.target.value)} />
          </Field>
          <Field label="Unit" htmlFor="p_unit">
            <Input id="p_unit" value={form.unit} onChange={(e) => set("unit")(e.target.value)} />
          </Field>
          <Field label="Reorder level" htmlFor="p_reorder">
            <Input
              id="p_reorder"
              type="number"
              min="0"
              value={form.reorder_level}
              onChange={(e) => set("reorder_level")(e.target.value)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Unit cost (₹)" htmlFor="p_cost">
            <Input id="p_cost" type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost")(e.target.value)} />
          </Field>
          <Field label="Sale price (₹)" htmlFor="p_price">
            <Input id="p_price" type="number" min="0" step="0.01" value={form.sale_price} onChange={(e) => set("sale_price")(e.target.value)} />
          </Field>
          {editing ? (
            <Field label="Status" htmlFor="p_status">
              <Select id="p_status" value={form.status} onChange={(e) => set("status")(e.target.value)}>
                {Object.entries(PRODUCT_STATUS_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field label="Opening stock" htmlFor="p_open">
              <Input id="p_open" type="number" min="0" value={form.opening_stock} onChange={(e) => set("opening_stock")(e.target.value)} />
            </Field>
          )}
        </div>
        <Field label="Preferred vendor" htmlFor="p_vendor">
          <Select id="p_vendor" value={form.preferred_vendor_id} onChange={(e) => set("preferred_vendor_id")(e.target.value)}>
            <option value="">None</option>
            {(vendors?.items ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={form.name.trim() === "" || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create product"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
